import { SandpackClient } from '@codesandbox/sandpack-client';
import webPreviewTemplate from './web-preview.tpl.html';
import webPreviewLoadErrorTemplate from './web-preview-load-error.tpl.html';
import webPreviewLoadErrorDialogTemplate from './web-preview-load-error-dialog.tpl.html';
import './web-preview.less';
import { codeHighlightComponentModule } from '../code-highlight/code-highlight';
import { hashCode } from '../../utils/string-utils';
import { windowEventHelperModule } from '../../helpers/window-event-helper/window-event-helper';
import { modalComponentModule } from '../modal/modal';
import { compileTemplateComponentModule } from '../compile-template/compile-template';
import { equivalentUrl } from '../../utils/dom-utils';
import { storageServiceModule } from '../../services/storage-service/storage-service';
import { trackerServiceModule } from '../../services/tracker-service/tracker-service';
import { webPreviewConsoleComponentModule } from './web-preview-console';
import throttle from 'lodash/throttle';

const RUNTIME_ERRROR_CHECKS_KEY = 'QualifiedWebPreviewErrors';
const DEFAULT_SANDPACK_VERSION = '0.0.54';
// Minimum Angular major version that requires the 2.19.8 bundler for web preview
const ANGULAR_MIN_VERSION_FOR_NEW_BUNDLER = 12;
const BUNDLER_URLS = {
  // smooshpack@0.0.54
  '0.0.54': 'https://qsp-0-0-54.qualified.io/',
  // smooshpack@0.0.66
  '0.0.66': 'https://qsp-0-0-66.qualified.io/',
  // smooshpack@1.0.0-alpha-31
  '1.0.0': 'https://qsp-1-0-0.qualified.io/',
  // @codesandbox/sandpack-client@0.10.11
  '0.10.11': 'https://qsp-0-10-11.qualified.io/',
  // @codesandbox/sandpack-client@1.20.9
  '1.20.9': 'https://qsp-1-20-9.qualified.io/',
  // @codesandbox/sandpack-client@2.19.8
  '2.19.8': 'https://qsp-2-19-8.web.app/',
};

const PRESET_SIZE = /(\d+)x(\d+)/;
export const RESPONSIVE_PRESETS = [
  ['smartphone-sm', 'Smartphone, Small', '320x568'],
  ['smartphone-md', 'Smartphone, Medium', '375x667'],
  ['smartphone-lg', 'Smartphone, Large', '414x738'],
  ['smartphone-xl', 'Smartphone, XL', '414x896'],
  ['tablet-portrait', 'Tablet, Portrait', '768x1024'],
  ['tablet-landscape', 'Tablet, Landscape', '1024x768'],
  ['hdtv-720', '720p HDTV', '1280x720'],
  ['hdtv-1080', '1080p HDTV', '1920x1080'],
];

const lastResponsiveModes = {};

let didShowLoadError = false;

/**
 * Inside the files for the web preview must be a package.json
 *
 * This package.json can contain customizations for the web preview renderer:
 *
 * @typedef webPreview~packageJson
 * @property {Object} webPreview - Contains web-preview customizations
 * @property {string} webPreview.bundlerURL - override the bundler URL to something custom. Must be a complete URL with protocol.
 * @property {string} webPreview.bundlerVersion - Use a custom version for sandpack. Recommended for published challenges. Currently supports 0.0.54, 0.0.66, and 1.0.0
 * @property {boolean} webPreview.migrateHeadContent - If true, moves external and inlined styles & scripts to the body tag. Otherwise, they are removed during bundling.
 * @property {string} webPreview.template - Override the dynamically selected template within sandpack.
 * @property {boolean} webPreview.skipEval - If true, don't actually evaluate code. Not sure what this is used for.
 */

/**
 * Moves externally referenced or inlined LINK, STYLE, or SCRIPT contents
 * to the body. The order is maintained as best as possible.
 *
 * Local referenced files are left as-is, or it causes a different set of errors.
 *
 * It's possible to change the semantics of the page this way, but it's the only way to make sure sandpack works as
 * expected, without losing critical information.
 *
 * @param html
 * @returns {string}
 * @private
 */
function _migrateHeadContent(html) {
  try {
    const mainNode = document.createElement('html');
    mainNode.innerHTML = html;
    const body = mainNode.querySelector('body');
    const nodes = mainNode.querySelectorAll(
      'head > script, head > link, head > style',
    );
    if (body && nodes.length) {
      Array.from(nodes)
        .reverse()
        .forEach((node) => {
          const src = node.getAttribute('href') || node.getAttribute('src');
          if (!src || /^(https?:)?\/\//.test(src)) {
            // moves the node if it's external or inlined
            body.prepend(node);
            body.prepend(document.createTextNode('\n'));
          }
        });
      // replace with new HTML
      html = mainNode.outerHTML;
    }
  } catch (ex) {}
  return html;
}

export const webPreviewComponentModule = angular
  .module('Q.components.web-preview', [
    codeHighlightComponentModule,
    windowEventHelperModule,
    modalComponentModule,
    compileTemplateComponentModule,
    storageServiceModule,
    trackerServiceModule,
    webPreviewConsoleComponentModule,
  ])
  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name webPreview
   * @description
   * Renders a web preview using sandpack-client
   *
   * @restrict E
   * @param {expression} files Array of files used to render. Should include package.json
   * @param {expression} onFocus Call back triggered when the iframe gets focus
   * @param {expression} setSyncFunction Provides a callback function called `sync` used to trigger a render on
   *                     changes (note: files will not be watched!)
   * @param {expression} autoSync set default value for when to auto sync on changes, track to remember across uses
   * @param {expression} responsiveMode Provides a default responsive mode value (either preset or /\d+x\d+/)
   * @param {expression} console stores the console log
   */
  .component('webPreview', {
    bindings: {
      // triggered when the iframe gets triggered
      onFocus: '&',
      // used to trigger a reload, so we're not watching files
      setSyncFunction: '&',
      // set default value for when to auto sync on changes
      autoSync: '=?',
      // sets the default a responsive mode value (ignored after startup)
      responsiveMode: '<',
      // Unique ID to use when preserving settings when opening & closing the tab
      uniqueKey: '<',
      // Allow the web preview to be force-disabled
      disableWebPreview: '<?',
    },
    controller: 'WebPreviewCtrl',
    template: webPreviewTemplate,
  })

  .controller(
    'WebPreviewCtrl',
    function WebPreviewCtrl(
      $scope,
      $element,
      $attrs,
      $timeout,
      highlightCode,
      $modal,
      $sessionStorage,
      tracker,
    ) {
      const ctrl = this;

      let client;
      let history = [];
      let historyPtr = -1;
      let currentUrl;
      let previewHash;
      if (!$sessionStorage[RUNTIME_ERRROR_CHECKS_KEY]) {
        $sessionStorage[RUNTIME_ERRROR_CHECKS_KEY] = {};
        $sessionStorage.$apply();
      }
      const runtimeErrorChecks = $sessionStorage[RUNTIME_ERRROR_CHECKS_KEY];

      const $outputIframe = $element.querySelector('iframe');

      const URL_PART_MATCHER = /^((https?:)?(\/\/)?[^\/]+\/?)/i;

      let syncBuffer;
      let lastUsedBundlerURL;
      let packageJsonParsed = { webPreview: {} };

      ctrl.loadingMessage = 'Building Preview';
      ctrl.loadErrorTemplate = webPreviewLoadErrorTemplate;

      let neverLoadedTimeout;
      let detectedLoadError;
      let loadErrorDialog;
      const _clearLoadError = () => {
        ctrl.neverLoaded = false;
        if (loadErrorDialog) {
          loadErrorDialog.dismiss();
          loadErrorDialog = null;
        }
      };
      const _showLoadError = () => {
        ctrl.neverLoaded = true;
        // eslint-disable-next-line angular/no-private-call
        if (!$scope.$$destroyed) {
          tracker.trackError('Web Preview Load Failed', {
            messageEventData:
              detectedLoadError === true ? null : detectedLoadError,
            bundlerURL: lastUsedBundlerURL,
            packageJSON: packageJsonParsed,
          });
          if (!didShowLoadError) {
            didShowLoadError = true;
            loadErrorDialog = $modal.openOnce('webPreviewLoadError', {
              scope: $scope,
              template: webPreviewLoadErrorDialogTemplate,
              windowClass: 'no-intercom',
            });
          }
        }
        ctrl.iframeSrc = $outputIframe[0].src;
      };
      const _resetLoaded = () => {
        $timeout.cancel(neverLoadedTimeout);
        detectedLoadError = false;
        ctrl.loaded = false;
        _clearLoadError();
        neverLoadedTimeout = $timeout(() => {
          if (!ctrl.loaded) {
            _showLoadError();
          }
        }, 15000);
      };

      const toFilesKey = (files) => {
        return Object.keys(files)
          .sort()
          .map((k) => `${k}/${files[k].code}`)
          .join('\n');
      };

      const getBundlerURL = (files) => {
        let version = DEFAULT_SANDPACK_VERSION;
        let url;
        const packageJson = files['/package.json'];
        if (packageJson) {
          try {
            packageJsonParsed = JSON.parse(packageJson.code) || {};
            packageJsonParsed.webPreview = packageJsonParsed.webPreview || {};
            const bundlerURL =
              packageJsonParsed.webPreview.bundlerURL ||
              packageJsonParsed.bundlerURL;
            const bundlerVersion =
              packageJsonParsed.webPreview.bundlerVersion ||
              packageJsonParsed.sandpackVersion;
            if (bundlerURL) {
              url = bundlerURL;
            } else if (/\d+\.\d+\.\d+/.test(bundlerVersion)) {
              // Explicit override from package.json — always takes priority
              version = bundlerVersion;
            } else {
              // Auto-detect: if Angular 12+ is in dependencies, use the 2.19.8 bundler to avoid pollyfills and zone.js issues
              // Else will have issues while using http/common
              const deps = Object.assign(
                {},
                packageJsonParsed.dependencies,
                packageJsonParsed.devDependencies,
              );
              const angularVersionStr = deps['@angular/core'];
              const angularMajor =
                angularVersionStr &&
                parseInt(angularVersionStr.replace(/[^0-9]/, ''), 10);
              if (
                angularMajor &&
                angularMajor >= ANGULAR_MIN_VERSION_FOR_NEW_BUNDLER
              ) {
                version = '2.19.8';
              }
            }
          } catch (err) {}
        }
        if (!url) {
          if (BUNDLER_URLS[version]) {
            url = BUNDLER_URLS[version];
          } else {
            // fallback to any codesandbox servers for testing purposes (should never be used on a final challenge)
            url = `https://sandpack-${version.replace(/\./g, '-')}.codesandbox.io`;
          }
        }
        const changed =
          client && lastUsedBundlerURL && url !== lastUsedBundlerURL;
        lastUsedBundlerURL = url;
        return { url, changed };
      };

      const clearRuntimeError = () => {
        if (previewHash) {
          runtimeErrorChecks[previewHash] = undefined;
          $sessionStorage.$apply();
        }
        ctrl.detectedError = false;
      };

      /**
       * Used to try to detect when a page has been reloaded while a web preview was open.
       * Useful in case of infinite loop bugs and other page lockup bugs.
       *
       * FIRST SYNC
       * If we are syncing for the first time, we check to see if the we ran a preview without ever getting
       * destroyed. If the hash exists in session the first time we try to sync, we stop the sync, and
       * show a warning message.
       *
       * FOLLOWUP SYNCS
       * If the user already approved running code, we unset the flag (assumption is everything is running OK),
       * and allow the page to render.
       * If they haven't, but the hash has changed, we unset the old hash.
       * After approval, we set the flag again.
       *
       * If an error was detected (on page load or somehow on subsequent syncs), we freeze the web preview until the candidate resumes it.
       *
       */
      const checkForRuntimeError = (files) => {
        if (window.parent !== window) {
          // workaround: don't check for runtime errors
          // when embedded to avoid false positives
          return false;
        }

        // will be unset on first load
        let oldPreviewHash = previewHash;

        // convert fileset into a hashcode
        previewHash = hashCode(JSON.stringify(files));

        // if this isn't the first load, and we haven't already detected an error or the hash changed,
        // clear the old hash. This allows the sync to happen on follow up attempts
        if (
          oldPreviewHash &&
          (!ctrl.detectedError || oldPreviewHash !== previewHash)
        ) {
          // unset the value on old hashes if they've changed, or we've already "approved" this code
          runtimeErrorChecks[oldPreviewHash] = undefined;
        }

        // if there's a bind to force-disable web preview, do so, then unset it
        if (ctrl.disableWebPreview) {
          ctrl.disableWebPreview = false;
          ctrl.detectedError = true;
        }

        // if we haven't already detected an error, see if we think one happened
        // will only be set on first loads when the file hashes haven't changed
        ctrl.detectedError =
          ctrl.detectedError || runtimeErrorChecks[previewHash];

        // flag that the web preview has run
        runtimeErrorChecks[previewHash] = 1;
        $sessionStorage.$apply();

        return ctrl.detectedError;
      };

      ctrl.resumePreview = () => {
        clearRuntimeError();
        ctrl.autoSync = true;
        ctrl.sync(true);
      };
      // Throttle reload to avoid overloading the rendering thread with too much work.
      // This should help avoid freezes, particularly in Project Code Challenges with Web Preview.
      const sync = (ctrl.sync = throttle((force) => {
        $timeout.cancel(syncBuffer);

        // eslint-disable-next-line angular/no-private-call
        if ($scope.$$destroyed || (!force && !ctrl.autoSync)) return;

        if (!force) {
          // wait until there's 1s of delay before syncing on auto renders
          syncBuffer = $timeout(() => {
            sync('buffered');
          }, 1000);
          return;
        }

        const challengeFiles = $scope.$parent.$eval($attrs.files);
        ctrl.showPreview =
          challengeFiles &&
          challengeFiles.length > 1 &&
          challengeFiles.some((f) => f.path === 'package.json');
        if (ctrl.showPreview) {
          const files = {};
          challengeFiles.forEach((f) => {
            files['/' + f.path] = {
              code: f.contents,
            };
          });

          if (checkForRuntimeError(files)) {
            ctrl.autoSync = false;
            if (client) {
              currentUrl = null;
              ctrl.reload();
            }
            return;
          }

          const bundlerURL = getBundlerURL(files);
          const webPreviewConfig = packageJsonParsed.webPreview;

          if (webPreviewConfig.migrateHeadContent) {
            angular.forEach(files, (data, path) => {
              if (/\.html?$/.test(path)) {
                data.code = _migrateHeadContent(data.code);
              }
            });
          }

          if (bundlerURL.changed) {
            currentUrl = null;
            ctrl.reload();
          } else if (client) {
            let shouldUpdate =
              force !== 'buffered' ||
              !client.sandboxInfo.files ||
              toFilesKey(client.sandboxInfo.files) !== toFilesKey(files);
            if (shouldUpdate) {
              client.sandboxInfo.files = files;
              client.updatePreview();
              if (force === 'reload') {
                ctrl.reload();
              }
            }
          } else {
            client = new SandpackClient(
              $outputIframe[0],
              {
                files,
                template: webPreviewConfig.template,
              },
              {
                bundlerURL: bundlerURL.url,
                showOpenInCodeSandbox: false,
                skipEval: webPreviewConfig.skipEval,
              },
            );
            _resetLoaded();
          }
        }
      }, 1000));

      const _resetHistory = () => {
        history.length = 0;
        historyPtr = -1;
      };

      const _updateCtrlUrl = () => {
        ctrl.path = currentUrl && currentUrl.replace(URL_PART_MATCHER, '');
        ctrl.lastPath = ctrl.path;
        ctrl.hasBack = historyPtr > 0;
        ctrl.hasForward = history.length - 1 > historyPtr;
      };
      const _loadUrl = (url) => {
        _resetLoaded();
        if (url) {
          $outputIframe[0].src = url;
        }
        currentUrl = url;
        _updateCtrlUrl();
        _resetHistory();
      };
      const _postMessage = (data) => {
        data.codesandbox = true;
        $outputIframe[0].contentWindow.postMessage(data, '*');
      };

      ctrl.loadErrorDismissed = false;
      ctrl.dismissLoadError = () => {
        ctrl.loadErrorDismissed = true;
      };

      ctrl.go = (path = ctrl.path) => {
        const urlParts = currentUrl && currentUrl.match(URL_PART_MATCHER);
        if (urlParts && urlParts[1]) {
          _loadUrl(urlParts[1] + path);
        }
      };

      ctrl.back = () => {
        if (historyPtr > 0) {
          _postMessage({ type: 'urlback' });
        }
      };

      ctrl.forward = () => {
        if (history.length - 1 > historyPtr) {
          _postMessage({ type: 'urlforward' });
        }
      };

      ctrl.home = () => {
        ctrl.go('');
      };

      ctrl.reload = () => {
        if (currentUrl) {
          _loadUrl(currentUrl);
        } else {
          // we never loaded successfully, completely reload
          if (client && angular.isFunction(client.cleanup)) {
            // unhook listener
            client.cleanup();
          }
          client = null;
          sync(true);
        }
      };

      ctrl.responsivePresets = RESPONSIVE_PRESETS.map(([id, label, size]) => {
        return {
          id,
          icon: id.split('-')[0],
          label,
          size,
          selected: false,
        };
      });

      let responsiveResizeListener;
      let $resizeWrapper;
      let lastResponsiveSize;
      let responsiveAnimation;

      const _updateResponsiveSize = (width, height, changeSize = true) => {
        width = Math.max(40, +width || 0);
        height = Math.max(40, +height || 0);
        ctrl.windowSize = {
          width: width,
          height: height,
          preset: null,
          landscape: width > height,
        };
        lastResponsiveSize = `${width}x${height}`;
        if (ctrl.uniqueKey) {
          lastResponsiveModes[ctrl.uniqueKey] = lastResponsiveSize;
        }
        ctrl.responsivePresets.forEach((preset) => {
          preset.selected = preset.size === lastResponsiveSize;
          if (preset.selected) {
            ctrl.windowSize.preset = preset;
          }
        });
        if (changeSize) {
          $resizeWrapper.addClass('resizing');
          $resizeWrapper.css({
            width: `${width + 20}px`,
            height: `${height + 20}px`,
          });
          $timeout.cancel(responsiveAnimation);
          responsiveAnimation = $timeout(
            () => {
              $resizeWrapper.removeClass('resizing');
            },
            250,
            false,
          );
        }
      };

      const _configureResizeListener = () => {
        if (!responsiveResizeListener) {
          const onResize = () => {
            if (
              ctrl.windowSize &&
              $resizeWrapper &&
              $resizeWrapper.isVisible('basic')
            ) {
              _updateResponsiveSize(
                $resizeWrapper.offsetWidth() - 20,
                $resizeWrapper.offsetHeight() - 20,
                false,
              );
            }
          };
          if (window.ResizeObserver) {
            responsiveResizeListener = new ResizeObserver(onResize);
            $timeout(() => {
              responsiveResizeListener.observe($resizeWrapper[0]);
            });
          } else if (window.MutationObserver) {
            responsiveResizeListener = new MutationObserver(onResize);
            $timeout(() => {
              responsiveResizeListener.observe($resizeWrapper[0], {
                attributes: true,
              });
            });
          }
        }
      };

      ctrl.toggleResponsiveMode = () => {
        let newSize;
        if (!ctrl.windowSize) {
          newSize = lastResponsiveSize || ctrl.responsivePresets[0].id;
        }
        ctrl.setResponsiveMode(newSize);
      };

      ctrl.setResponsiveMode = (value) => {
        if (!$resizeWrapper) {
          $resizeWrapper = $element.querySelector('.responsive-resize-wrapper');
        }
        ctrl.responsivePresets.forEach((p) => (p.selected = false));
        ctrl.windowSize = null;
        let size = null;
        if (responsiveResizeListener) {
          responsiveResizeListener.disconnect();
          responsiveResizeListener = null;
        }
        if (PRESET_SIZE.test(value)) {
          size = value;
        } else if (value) {
          const preset = ctrl.responsivePresets.find((p) => p.id === value);
          if (preset) {
            size = preset.size;
          }
        }
        if (size) {
          const [, width, height] = size.match(PRESET_SIZE);
          _updateResponsiveSize(width, height);
          _configureResizeListener();
        } else {
          if (ctrl.uniqueKey) {
            delete lastResponsiveModes[ctrl.uniqueKey];
          }
          $resizeWrapper.css({
            width: null,
            height: null,
          });
        }
      };

      ctrl.customResponsiveMode = () => {
        if (
          ctrl.windowSize &&
          ctrl.windowSize.width >= 40 &&
          ctrl.windowSize.height > 40
        ) {
          ctrl.setResponsiveMode(
            `${ctrl.windowSize.width}x${ctrl.windowSize.height}`,
          );
        }
      };

      ctrl.rotateResponsiveMode = () => {
        if (ctrl.windowSize) {
          ctrl.setResponsiveMode(
            `${ctrl.windowSize.height}x${ctrl.windowSize.width}`,
          );
        }
      };

      const handleUrlChange = (data) => {
        // handle history navigation
        let popped = false;
        if (data.action === 'POP') {
          const newPtr = historyPtr + data.diff;
          popped = history[newPtr] === data.url;
          if (popped) {
            historyPtr = newPtr;
          } else {
            // we're out of sync, treat it as a reset/reload
            _resetHistory();
          }
        }

        // handle other loading scenarios
        if (!popped) {
          if (historyPtr > -1) {
            historyPtr =
              data.action === 'REPLACE' ? historyPtr : historyPtr + 1;
            // drop all future states
            history = history.slice(0, historyPtr);
          } else {
            historyPtr = 0;
          }
          history.push(data.url);
        }

        currentUrl = data.url;
        _updateCtrlUrl();
      };

      const STATUS_MESSAGES = {
        'installing-dependencies': 'Installing Dependencies',
        transpiling: 'Transpiling Code',
        evaluating: 'Evaluating Code',
        idle: 'Ready',
      };

      ctrl.$onInit = () => {
        if (ctrl.autoSync === undefined) {
          ctrl.autoSync = true;
        }
        ctrl.hasConsole = !!ctrl.console;
        // set up focus
        $scope.$on(
          'document:focusoutActiveElement',
          ($e, event, activeElement) => {
            if ($outputIframe[0] && activeElement === $outputIframe[0]) {
              ctrl.onFocus();
            }
          },
        );

        $scope.$on('window:message', ($event, messageEvent) => {
          const data = messageEvent && messageEvent.data;
          if (data && equivalentUrl(messageEvent.origin, lastUsedBundlerURL)) {
            const type = data.type;
            if (type === 'initialized') {
              // something triggered a full page reload
              if (ctrl.loaded) {
                // change loading message
                ctrl.loadingMessage = 'Reloading Preview';
              }
              ctrl.statusMessage = '';
              _resetLoaded();
              _resetHistory();
            } else if (type === 'status') {
              ctrl.statusMessage = STATUS_MESSAGES[data.status];
            } else if (type === 'start') {
              ctrl.syncing = true;
            } else if (type === 'done') {
              if (!ctrl.loaded) {
                if (!detectedLoadError) {
                  tracker.trackEvent('Web Preview Load Succeeded', {
                    bundlerURL: lastUsedBundlerURL,
                    packageJSON: packageJsonParsed,
                  });
                }
              }
              ctrl.loaded = true;
              if (detectedLoadError) {
                _showLoadError();
              } else {
                _clearLoadError();
              }
              ctrl.syncing = false;
            } else if (type === 'urlchange') {
              handleUrlChange(data);
            }
          }
        });

        $scope.$on('$destroy', () => {
          if (client && angular.isFunction(client.cleanup)) {
            // unhook listener
            client.cleanup();
          }
          // clear sync reference
          ctrl.setSyncFunction({ sync: null });
          clearRuntimeError();
          if (responsiveResizeListener) {
            responsiveResizeListener.disconnect();
          }
        });
        ctrl.setSyncFunction({ sync });
        sync(true);

        const rMode =
          (ctrl.uniqueKey && lastResponsiveModes[ctrl.uniqueKey]) ||
          ctrl.responsiveMode;
        ctrl.setResponsiveMode(rMode);
      };
    },
  );
